import {
  Rocket,
  Cpu,
  Download,
  AlertTriangle,
  Lock,
  Users,
  Building2,
  Eye,
  LogIn,
  type LucideIcon,
} from 'lucide-react';

export type AuditTone = 'pass' | 'warn' | 'block' | 'info' | '';
export type AuditType = 'prest' | 'auto' | 'doc' | 'billing' | 'sec' | 'user' | 'ws';

export interface AuditDisplay {
  icon: LucideIcon;
  tone: AuditTone;
  type: AuditType;
  /** Frase humanizada — preserva o `target` quando faz sentido. */
  label: string;
}

/**
 * Traduz `audit_log.action` (snake.case) pra ícone+tone+frase humana.
 * Fallback: ícone neutro + ação crua.
 */
export function describeAuditAction(action: string, metadata?: Record<string, unknown> | null): AuditDisplay {
  switch (action) {
    case 'workspace.impersonate_start':
      return { icon: Eye, tone: 'warn', type: 'sec', label: 'iniciou impersonação' };
    case 'workspace.impersonate_end':
      return { icon: Eye, tone: 'info', type: 'sec', label: 'encerrou impersonação' };
    case 'workspace.created':
      return {
        icon: Building2,
        tone: 'info',
        type: 'ws',
        label: `criou workspace ${metadata?.slug ? `(${String(metadata.slug)})` : ''}`.trim(),
      };
    case 'workspace.suspended':
      return { icon: Lock, tone: 'warn', type: 'sec', label: 'suspendeu o workspace' };
    case 'workspace.reactivated':
      return { icon: Rocket, tone: 'pass', type: 'sec', label: 'reativou o workspace' };
    case 'prestacao.efetivada':
      return { icon: Rocket, tone: 'pass', type: 'prest', label: 'efetivou a prestação' };
    case 'prestacao.cancelada':
      return { icon: AlertTriangle, tone: 'block', type: 'prest', label: 'cancelou a prestação' };
    case 'automation.completed':
      return { icon: Cpu, tone: 'info', type: 'auto', label: 'automação concluída' };
    case 'document.exported':
      return { icon: Download, tone: '', type: 'doc', label: 'exportou documento' };
    case 'invoice.overdue':
      return { icon: AlertTriangle, tone: 'block', type: 'billing', label: 'fatura vencida' };
    case 'user.invited':
      return { icon: Users, tone: '', type: 'user', label: 'convidou usuário' };
    case 'user.login':
      return { icon: LogIn, tone: 'info', type: 'user', label: 'fez login' };
    default:
      return { icon: AlertTriangle, tone: '', type: 'ws', label: action.replace(/[._]/g, ' ') };
  }
}

export function formatRelativeTime(iso: string, now = new Date()): string {
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ontem';
  if (d < 30) return `há ${d} dias`;
  const months = Math.floor(d / 30);
  if (months < 12) return `há ${months} m`;
  return `há ${Math.floor(months / 12)} a`;
}
